import type { TicketItem } from '../types';
import { SERVICE_CATEGORY_META } from '../utils/constants';
import { useDepartments } from '../hooks/useDepartments';

interface Props {
  item:      Partial<TicketItem>;
  index:     number;
  onChange:  (updated: Partial<TicketItem>) => void;
  onRemove:  () => void;
  canRemove?: boolean;
  // Field name -> error message for THIS item, as returned by the server for
  // items.<index>.<field> (see NewTicket.tsx's submit error handling). Highlights
  // the exact input and shows the exact reason, instead of one generic banner.
  errors?: Record<string, string>;
}

const fc = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-600 transition-all bg-white disabled:bg-gray-50';
const fcErr = 'w-full text-sm border border-red-400 rounded-lg px-3 py-2 outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-all bg-white disabled:bg-gray-50';
const lc = 'block text-xs font-medium text-gray-500 mb-1';

function ErrMsg({ error }: { error?: string }) {
  if (!error) return null;
  return <span className="block text-xs text-red-600 mt-1">{error}</span>;
}

function Sel({ label, value, onChange, options, required, error }: {
  label: string; value: string; onChange: (v: string) => void;
  options: string[]; required?: boolean; error?: string;
}) {
  return (
    <div>
      <label className={lc}>{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      <select className={error ? fcErr : fc} value={value} onChange={e => onChange(e.target.value)} required={required}>
        <option value="">Select…</option>
        {options.map(o => <option key={o}>{o}</option>)}
      </select>
      <ErrMsg error={error} />
    </div>
  );
}

function Txt({ label, value, onChange, placeholder, required, readOnly, error, type = 'text' }: {
  label: string; value: string; onChange?: (v: string) => void;
  placeholder?: string; required?: boolean; readOnly?: boolean; error?: string; type?: 'text' | 'number';
}) {
  return (
    <div>
      <label className={lc}>{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      <input type={type} min={type === 'number' ? 1 : undefined}
        className={error ? fcErr : fc} value={value} onChange={e => onChange?.(e.target.value)}
        placeholder={placeholder} required={required} readOnly={readOnly} />
      <ErrMsg error={error} />
    </div>
  );
}

function Area({ label, value, onChange, placeholder, rows = 3, required, error }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; rows?: number; required?: boolean; error?: string;
}) {
  return (
    <div>
      <label className={lc}>{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      <textarea className={`${error ? fcErr : fc} resize-none`} rows={rows} value={value}
        onChange={e => onChange(e.target.value)} placeholder={placeholder} required={required} />
      <ErrMsg error={error} />
    </div>
  );
}

function DateIn({ label, value, onChange, error }: { label: string; value: string; onChange: (v: string) => void; error?: string }) {
  return (
    <div>
      <label className={lc}>{label}</label>
      <input type="date" className={error ? fcErr : fc} value={value} onChange={e => onChange(e.target.value)} />
      <ErrMsg error={error} />
    </div>
  );
}

function CategoryFields({ item, upd, errors }: {
  item: Partial<TicketItem>; upd: (k: string, v: string) => void; errors: Record<string, string>;
}) {
  const cat = item.category || '';
  const g   = (k: string) => String((item as any)[k] ?? '');
  const { names: departmentNames } = useDepartments();

  if (cat === 'Access Management') return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Sel label="Access Type" value={g('accessType')} onChange={v => upd('accessType', v)}
          options={['Folder Access', 'SharePoint', 'Shared Drive', 'VPN', 'Application Login', 'Database', 'Cloud Service Access']}
          required error={errors.accessType} />
        <Txt label="Resource Name / Path" value={g('resourcePath')} onChange={v => upd('resourcePath', v)}
          placeholder="e.g. \\server\Finance\Reports" error={errors.resourcePath} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Sel label="Access Level" value={g('accessLevel')} onChange={v => upd('accessLevel', v)}
          options={['Read', 'Read-Write', 'Full Control']} error={errors.accessLevel} />
        <Sel label="New or Existing User" value={g('userType')} onChange={v => upd('userType', v)}
          options={['New User', 'Existing User']} error={errors.userType} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Sel label="Duration" value={g('duration')} onChange={v => upd('duration', v)}
          options={['Permanent', 'Temporary']} error={errors.duration} />
        {g('duration') === 'Temporary' && (
          <DateIn label="End Date" value={g('endDate')} onChange={v => upd('endDate', v)} error={errors.endDate} />
        )}
      </div>
    </div>
  );

  if (cat === 'Email & Account Services') return (
    <div className="space-y-3">
      <Sel label="Service Type" value={g('emailService')} onChange={v => upd('emailService', v)}
        options={[
          'New Email Account', 'Distribution List', 'Shared Mailbox',
          'Email Alias', 'Password Reset', 'Account Unlock', 'Account Deactivation',
        ]} required error={errors.emailService} />
      <div className="grid grid-cols-2 gap-3">
        <Txt label="Full Name" value={g('fullName')} onChange={v => upd('fullName', v)}
          placeholder="For new account" error={errors.fullName} />
        <Txt label="Preferred Username" value={g('prefUsername')} onChange={v => upd('prefUsername', v)}
          placeholder="e.g. john.doe" error={errors.prefUsername} />
      </div>
      <Sel label="M365 License" value={g('licenseType')} onChange={v => upd('licenseType', v)}
        options={['M365 Basic', 'M365 Business Standard', 'M365 Business Premium', 'M365 E3', 'M365 E5']}
        error={errors.licenseType} />
    </div>
  );

  if (cat === 'Software & Application') return (
    <div className="space-y-3">
      <Txt label="Tool / App URL" value={g('toolUrl')} onChange={v => upd('toolUrl', v)}
        placeholder="https://…" required error={errors.toolUrl} />
      <div className="grid grid-cols-2 gap-3">
        <Txt label="Application Name" value={g('appName')} onChange={v => upd('appName', v)}
          placeholder="e.g. Adobe Acrobat" required error={errors.appName} />
        <Txt label="Version" value={g('appVersion')} onChange={v => upd('appVersion', v)}
          placeholder="Latest or specific" error={errors.appVersion} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Sel label="Install Type" value={g('installType')} onChange={v => upd('installType', v)}
          options={['New Install', 'Upgrade', 'Uninstall', 'Reinstall']} error={errors.installType} />
        <Txt label="Device / Hostname" value={g('deviceName')} onChange={v => upd('deviceName', v)}
          placeholder="e.g. PC-FIN-001" error={errors.deviceName} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Txt label="No. of Devices" value={g('numDevices')} onChange={v => upd('numDevices', v)}
          placeholder="1" type="number" error={errors.numDevices} />
        <Sel label="OS" value={g('os')} onChange={v => upd('os', v)}
          options={['Windows 10', 'Windows 11', 'macOS', 'Linux']} error={errors.os} />
      </div>
    </div>
  );

  if (cat === 'Hardware & Equipment') return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Sel label="Equipment Type" value={g('equipType')} onChange={v => upd('equipType', v)}
          options={[
            'Laptop', 'Desktop', 'Monitor', 'Printer', 'IP Phone',
            'Headset', 'USB Drive', 'Webcam', 'Docking Station', 'Other',
          ]} required error={errors.equipType} />
        <Sel label="Request Type" value={g('hwReqType')} onChange={v => upd('hwReqType', v)}
          options={['New', 'Replacement', 'Repair', 'Return']} error={errors.hwReqType} />
      </div>
      {(['Replacement', 'Repair'] as string[]).includes(g('hwReqType')) && (
        <Txt label="Asset Tag" value={g('assetTag')} onChange={v => upd('assetTag', v)} error={errors.assetTag} />
      )}
      <Txt label="Delivery Location" value={g('deliveryLoc')} onChange={v => upd('deliveryLoc', v)}
        placeholder="Floor / Room" error={errors.deliveryLoc} />
    </div>
  );

  if (cat === 'Server & Infrastructure') return (
    <div className="space-y-3">
      <Sel label="Service Type" value={g('serverService')} onChange={v => upd('serverService', v)}
        options={[
          'New VM', 'Server Configuration', 'Backup & Restore', 'Storage Allocation',
          'DNS Record', 'Firewall Rule', 'SSL Certificate', 'Database Setup',
        ]} required error={errors.serverService} />
      <div className="grid grid-cols-2 gap-3">
        <Txt label="Server Name / IP" value={g('serverName')} onChange={v => upd('serverName', v)} error={errors.serverName} />
        <Sel label="Environment" value={g('environment')} onChange={v => upd('environment', v)}
          options={['Production', 'Staging', 'Development', 'Test']} error={errors.environment} />
      </div>
      <Area label="Config Details" value={g('configDetails')} onChange={v => upd('configDetails', v)} error={errors.configDetails} />
      <Sel label="Downtime OK?" value={g('downtimeOk')} onChange={v => upd('downtimeOk', v)}
        options={['Yes', 'No']} error={errors.downtimeOk} />
    </div>
  );

  if (cat === 'Network & Connectivity') return (
    <div className="space-y-3">
      <Sel label="Request Type" value={g('netReqType')} onChange={v => upd('netReqType', v)}
        options={[
          'New Network Point', 'WiFi Access', 'Static IP',
          'Port Opening', 'VPN Setup', 'VLAN Configuration',
        ]} required error={errors.netReqType} />
      <div className="grid grid-cols-2 gap-3">
        <Txt label="Location / Room" value={g('netLocation')} onChange={v => upd('netLocation', v)} error={errors.netLocation} />
        <Txt label="Device Type" value={g('netDevice')} onChange={v => upd('netDevice', v)} error={errors.netDevice} />
      </div>
    </div>
  );

  if (cat === 'New User Onboarding') return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Txt label="Employee Full Name" value={g('newEmpName')} onChange={v => upd('newEmpName', v)}
          required error={errors.newEmpName} />
        <DateIn label="Start Date" value={g('startDate')} onChange={v => upd('startDate', v)} error={errors.startDate} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Txt label="Job Title" value={g('newJobTitle')} onChange={v => upd('newJobTitle', v)} error={errors.newJobTitle} />
        <Sel label="Department" value={g('newDept')} onChange={v => upd('newDept', v)}
          options={departmentNames} error={errors.newDept} />
      </div>
      <Txt label="Line Manager" value={g('newManager')} onChange={v => upd('newManager', v)} error={errors.newManager} />
      <Area label="Required Applications" value={g('reqApps')} onChange={v => upd('reqApps', v)} error={errors.reqApps} />
      <Txt label="Hardware Required" value={g('reqHardware')} onChange={v => upd('reqHardware', v)} error={errors.reqHardware} />
    </div>
  );

  if (cat === 'AI Tool / Application Request') return (
    <div className="space-y-3">
      <Txt label="Tool URL" value={g('toolUrl')} onChange={v => upd('toolUrl', v)}
        placeholder="https://…" required error={errors.toolUrl} />
      <Txt label="AI Tool Name" value={g('aiToolName')} onChange={v => upd('aiToolName', v)} required error={errors.aiToolName} />
      <div className="grid grid-cols-2 gap-3">
        <Sel label="Deployment Mode" value={g('deploymentMode')} onChange={v => upd('deploymentMode', v)}
          options={['Cloud SaaS', 'Browser Extension', 'API Integration', 'Isolated Sandbox', 'On-Premise']}
          required error={errors.deploymentMode} />
        <Sel label="Data Access Level" value={g('dataAccessLevel')} onChange={v => upd('dataAccessLevel', v)}
          options={['No sensitive data', 'Internal data', 'Confidential or PII', 'Customer data']}
          required error={errors.dataAccessLevel} />
      </div>
      <Sel label="Third-Party Data Sharing" value={g('thirdPartyDataSharing')} onChange={v => upd('thirdPartyDataSharing', v)}
        options={['Yes', 'No']} required error={errors.thirdPartyDataSharing} />
      <Txt label="Integration Points" value={g('integrationPoints')} onChange={v => upd('integrationPoints', v)}
        placeholder="e.g. CRM, Email, LinkedIn" error={errors.integrationPoints} />
    </div>
  );

  if (cat === 'Cloud Services Access') return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Sel label="Cloud Provider" value={g('cloudProvider')} onChange={v => upd('cloudProvider', v)}
          options={['AWS', 'Azure', 'GCP', 'DigitalOcean', 'Other']} required error={errors.cloudProvider} />
        <Sel label="Resource Type" value={g('resourceType')} onChange={v => upd('resourceType', v)}
          options={['Storage', 'Compute', 'Database', 'Networking', 'Other']} required error={errors.resourceType} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Sel label="Access Level Requested" value={g('accessLevelRequested')} onChange={v => upd('accessLevelRequested', v)}
          options={['Read-only', 'Read-Write', 'Admin']} required error={errors.accessLevelRequested} />
        <Sel label="Data Classification" value={g('dataClassification')} onChange={v => upd('dataClassification', v)}
          options={['Public', 'Internal', 'Confidential', 'Restricted']} required error={errors.dataClassification} />
      </div>
    </div>
  );

  if (cat === 'Access Request') return (
    <div className="space-y-3">
      <Txt label="System / App Name" value={g('systemOrAppName')} onChange={v => upd('systemOrAppName', v)}
        required error={errors.systemOrAppName} />
      <div className="grid grid-cols-2 gap-3">
        <Sel label="Access Level" value={g('accessLevelRequested')} onChange={v => upd('accessLevelRequested', v)}
          options={['Read-only', 'Read-Write', 'Admin']} required error={errors.accessLevelRequested} />
        <Sel label="Data Classification" value={g('dataClassification')} onChange={v => upd('dataClassification', v)}
          options={['Public', 'Internal', 'Confidential', 'Restricted']} required error={errors.dataClassification} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Sel label="Duration" value={g('duration')} onChange={v => upd('duration', v)}
          options={['Permanent', 'Temporary']} required error={errors.duration} />
        {g('duration') === 'Temporary' && (
          <DateIn label="End Date" value={g('endDate')} onChange={v => upd('endDate', v)} error={errors.endDate} />
        )}
      </div>
    </div>
  );

  if (cat === 'Infrastructure / Server Change') return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Sel label="Change Type" value={g('changeType')} onChange={v => upd('changeType', v)}
          options={['New', 'Modify', 'Decommission']} required error={errors.changeType} />
        <Sel label="Environment" value={g('environment')} onChange={v => upd('environment', v)}
          options={['Production', 'Staging', 'Development']} required error={errors.environment} />
      </div>
      <Txt label="System / Server Name" value={g('systemOrServerName')} onChange={v => upd('systemOrServerName', v)}
        required error={errors.systemOrServerName} />
    </div>
  );

  if (cat === 'Third-Party Vendor Onboarding') return (
    <div className="space-y-3">
      <Txt label="Vendor Name" value={g('vendorName')} onChange={v => upd('vendorName', v)} required error={errors.vendorName} />
      <div className="grid grid-cols-2 gap-3">
        <Sel label="Data Shared With Vendor" value={g('dataSharedWithVendor')} onChange={v => upd('dataSharedWithVendor', v)}
          options={['None', 'Internal', 'Confidential', 'Customer PII']} required error={errors.dataSharedWithVendor} />
        <Sel label="Criticality" value={g('criticality')} onChange={v => upd('criticality', v)}
          options={['Low', 'Medium', 'High', 'Critical']} required error={errors.criticality} />
      </div>
    </div>
  );

  if (cat === 'Data Export / Sharing Request') return (
    <div className="space-y-3">
      <Area label="Data Being Exported" value={g('dataBeingExported')} onChange={v => upd('dataBeingExported', v)}
        required error={errors.dataBeingExported} />
      <div className="grid grid-cols-2 gap-3">
        <Txt label="Destination" value={g('destination')} onChange={v => upd('destination', v)}
          required error={errors.destination} />
        <Txt label="Recipient" value={g('recipient')} onChange={v => upd('recipient', v)}
          required error={errors.recipient} />
      </div>
      <Area label="Purpose" value={g('exportPurpose')} onChange={v => upd('exportPurpose', v)}
        required error={errors.exportPurpose} />
    </div>
  );

  // General / Other
  return (
    <div className="space-y-3">
      <Txt label="Request Title" value={g('otherTitle')} onChange={v => upd('otherTitle', v)} required error={errors.otherTitle} />
      <Area label="Detailed Description" value={g('otherDesc')} onChange={v => upd('otherDesc', v)} rows={4} error={errors.otherDesc} />
    </div>
  );
}

export default function ServiceItemForm({ item, index, onChange, onRemove, canRemove, errors = {} }: Props) {
  const upd = (k: string, v: string) => onChange({ ...item, [k]: v });

  return (
    <div className="border border-gray-200 rounded-xl p-4 bg-[#F4F4F8]">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-[#010E23]">Item {index + 1}</span>
        {canRemove && (
          <button type="button" onClick={onRemove}
            className="text-xs text-gray-400 hover:text-red-600 border border-gray-200 rounded px-2 py-0.5 transition-colors bg-white">
            ✕ Remove
          </button>
        )}
      </div>

      {/* Category selector */}
      <div className="mb-3">
        <label className={lc}>Service Category <span className="text-red-500">*</span></label>
        <select className={errors.category ? fcErr : fc} value={item.category || ''}
          onChange={e => onChange({ ...item, category: e.target.value })} required>
          <option value="">Select service category…</option>
          {SERVICE_CATEGORY_META.map(c => (
            <option key={c.id} value={c.label}>{c.icon}  {c.label}</option>
          ))}
        </select>
        <ErrMsg error={errors.category} />
      </div>

      {/* Category-specific fields */}
      {item.category && (
        <div className="mb-3">
          <CategoryFields item={item} upd={upd} errors={errors} />
        </div>
      )}

      {/* Notes — shown for all categories */}
      <Area label="Justification / Notes" value={String((item as any).notes ?? '')}
        onChange={v => upd('notes', v)} placeholder="Why is this item needed?" rows={2} error={errors.notes} />
    </div>
  );
}
